import { AnimatePresence, motion } from "motion/react";

import { ButtonHTMLAttributes, JSX, ReactNode, forwardRef } from "react";

import { cn } from "@/utils/cn.ts";

import Spinner from "../helpers/Spinner/Spinner";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  type?: "submit" | "button" | "reset";
  icon?: JSX.Element;
  size?: "large" | "medium" | "small";
  children: ReactNode;
  color?: "orange" | "black" | "white";
  className?: string;
  isLoading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      type = "button",
      size = "large",
      icon,
      children,
      color = "orange",
      className,
      isLoading = false,
      disabled = false,
      ...otherProps
    },
    ref,
  ): JSX.Element => {
    return (
      <button
        {...otherProps}
        ref={ref}
        type={type}
        disabled={disabled || isLoading}
        className={cn(
          "responsive-transition group w-full rounded-full px-8 font-semibold transition-all hover:bg-orange-3 active:bg-orange-1",
          "max-lg:text-sm max-lg:font-semibold max-lg:leading-4",
          {
            "py-[1.125rem] text-base/normal": size === "large",
            "py-3 text-sm/6": size === "medium",
            "w-min p-5": size === "small",
            "disabled:text-on-surface bg-orange-1 text-white disabled:bg-orange-2": color === "orange",
            "disabled:text-on-surface bg-surface-container disabled:bg-surface-container-high text-on-primary":
              color === "black",
            "disabled:text-on-surface disabled:bg-surface-container-high text-on-primary hover:bg-on-primary active:bg-on-primary bg-white-opacity-2 font-semibold hover:text-orange-1 active:text-orange-1":
              color === "white",
            "flex items-center justify-center gap-2": icon || isLoading,
          },
          className,
        )}
      >
        {icon ||
          (isLoading && (
            <span
              className={cn("[&>svg]:transition-colors", {
                "[&>svg]:fill-on-surface": disabled && color !== "white",
                "[&>svg]:fill-on-primary": color !== "white",
                "[&>svg]:stroke-on-primary group-hover:[&>svg]:stroke-orange-1": color === "white",
                "[&>svg]:fill-on-surface [&>svg]:stroke-on-surface": disabled && color === "white",
              })}
            >
              <AnimatePresence>
                {isLoading ? (
                  <motion.div
                    initial={{ width: "0rem", opacity: 0 }}
                    animate={{ width: "1.5rem", opacity: 1 }}
                    exit={{ width: "0rem", opacity: 0 }}
                  >
                    <Spinner className="h-6 w-6" />
                  </motion.div>
                ) : (
                  icon
                )}
              </AnimatePresence>
            </span>
          ))}
        {children}
      </button>
    );
  },
);

Button.displayName = "Button";

export default Button;
